import axios from "axios";
import { URL_LOGIN_V2, URL_LOGOUT, URL_USERS } from "constants/urlApis";
import { endpointError } from "utils/codeErrorsTimeout";

/**
 * Sends a login request to the server with the provided username and password.
 * @param {string} username - The username for login.
 * @param {string} password - The password for login.
 * @returns {Promise<Object>} The response data from the server if the request is successful.
 * @throws {Error} Throws an error if the request fails.
 */
export const loginService = async (username, password) => {
  return await axios
    .post(
      URL_LOGIN_V2,
      { user_name: username.toLowerCase(), password: password },
      {
        endpointError: endpointError.login.loginService,
      }
    )
    .then((response) => {
      if (response) return response.data;
    });
};

/**
 * Fetches the permissions associated with the authenticated user.
 * Makes a GET request to the `/users/permissions/` endpoint using the
 * provided JWT token. Returns the permissions data from the API response.
 * @param {string} jwt - The JSON Web Token used for authentication. It must be a valid token, as it will be included in the Authorization header.
 * @returns {Promise<Object>} A promise that resolves to the user's permissions data returned by the API.
 * @throws {Error} Throws an error if the request fails or the token is invalid.
 */
export const userPermissions = async (jwt) => {
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };
  const response = await axios.get(`${URL_USERS}/users/permissions/`, {
    headers,
    endpointError: endpointError.login.userPermissions,
  });
  return response.data;
};

/**
 * Retrieves the user settings using the provided JWT token.
 * @param {string} jwt - The JSON Web Token used for authorization.
 * @returns {Promise<any>} A promise that resolves to the user settings data returned by the server.
 * @throws {Error} Throws an error if the request fails or the server responds with an error.
 */
export const userSettings = async (jwt) => {
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };
  const response = await axios.get(`${URL_USERS}/settings/`, {
    headers,
    endpointError: endpointError.login.userSettings,
  });
  return response.data;
};

/**
 * Sends a logout request to the server with the provided JWT token.
 * @param {string} jwt - The JWT token for authorization.
 * @returns {Promise<Object>} The response from the server.
 */
export const logoutService = async (jwt) => {
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };
  const response = await axios.post(
    URL_LOGOUT,
    {},
    {
      headers,
      endpointError: endpointError.login.loginService,
    }
  );
  return response;
};
